import { fetchApiSaga } from '@hozana/api/sagas'
import { all, delay, put, race, take, takeLatest } from '@hozana/sagas'
import type { TSagaProps } from '@hozana/sagas/types'
import { cookie } from '@hozana/storage/cookies'
import { GTM } from '@hozana/tracking/gtm'

import { openPopin } from 'general/actions'
import { AppActionTypes } from 'general/constants'
import { COOKIES_CONFIG } from 'general/managers/cookies/constants'
import { COOKIES } from 'general/managers/cookies/cookieNames'
import { POPINS } from 'general/managers/popins/constants'
import { TBackToPopin } from 'general/managers/popins/types'

import { TUserPrayPopinOptions } from 'modules/pray/popins/UserPrayPopin'

import { PRAY_OBJECT_TYPE, TIME_TO_PRAY } from './constants'
import { TPrayMutationPayload, prayMutation } from './queries'
import { prayUser } from './routines'

/*
 * UTILS
 */

export type TRunUserPrayProps = TUserPrayPopinOptions & {
  trackingSource: string
  backToPopin?: TBackToPopin
}

export function* runUserPray({
  backToPopin,
  trackingSource,
  ...prayPopinOptions
}: TRunUserPrayProps): Generator<unknown, boolean> {
  GTM.trackEvent(GTM.EVENTS.PRIERE_START, { trackingSource })

  yield* put(openPopin(POPINS.UserPrayPopin, prayPopinOptions, backToPopin))

  const { onTimeout } = yield* race({
    onTimeout: delay(TIME_TO_PRAY),
    onClose: take(AppActionTypes.CLOSE_POPIN),
  })

  if (onTimeout) {
    GTM.trackEvent(GTM.EVENTS.PRIERE_FINISHED, { trackingSource })
    return true
  } else {
    GTM.trackEvent(GTM.EVENTS.PRIERE_INTERRUPTED, { trackingSource })
    return false
  }
}

/*
 * SAGAS
 */

export type TPrayUserPayload = Pick<TPrayMutationPayload, 'widgetId' | 'guestName'> &
  Omit<TRunUserPrayProps, 'objectType'> & {
    objectType: Exclude<TRunUserPrayProps['objectType'], PRAY_OBJECT_TYPE.USER>
  }

function* prayUserSaga({
  payload: { objectId, objectType, trackingSource, widgetId, guestName, backToPopin },
}: TSagaProps<TPrayUserPayload>) {
  try {
    const hasPrayed = yield* runUserPray({ trackingSource, objectId, objectType, backToPopin })

    if (hasPrayed) {
      cookie.save(COOKIES.hasPrayedForAnIntention, true, COOKIES_CONFIG.ONE_YEAR)
      yield* fetchApiSaga(prayMutation({ objectId, objectType, widgetId, guestName }))
    } else {
      yield* put(prayUser.failure('cancel'))
    }

    yield* put(prayUser.success({ objectId, objectType }))
  } catch (error) {
    yield* put(prayUser.failure(error.message || error))
  }
}

/*
 * LISTENER
 */

export default function* praySagasListener() {
  yield* all([takeLatest(prayUser, prayUserSaga)])
}
